<script setup>
import { computed, onMounted, ref } from "vue"

import Lottie from "@/components/widgets/lottie.vue"
import animationData from "@/components/widgets/msoeawqm.json"
import PersianDate from "persian-date"

import store from "../../state/store"
import useProducts from "../../use/useProducts"
import useDebouncedFunction from "../../use/useDebouncedFunction"
import TableHeaderCell from "@/components/table/TableHeaderCell.vue"

const products = computed(() => store.state.products)
const categoriesWithProducts = computed(
  () => store.state.categoriesWithProducts
)
const productCreators = computed(() => store.state.productCreators)

const {
  productStatus,
  search,
  categoryId,
  createdBy,
  perPage,
  sortField,
  sortDirection,
  getProducts,
  sortProducts,
} = useProducts()
const { handleInput } = useDebouncedFunction(getProducts)

const formattedPrices = computed(() => {
  return products.value.data.map((product) =>
    new Intl.NumberFormat("fa-IR").format(product.price)
  )
})

const formattedDiscount = computed(() => {
  return products.value.data.map((product) =>
    new Intl.NumberFormat("fa-IR").format(product.discount)
  )
})

onMounted(() => {
  getProducts()
  store.dispatch("getCategoriesWithProducts")
  store.dispatch("getCreatorsWithProducts")
})

const toPersianDateTime = (date) => {
  const [datePart, timePart] = date.split(" ")
  const [year, month, day] = datePart.split("-")
  const [hour, minute, second] = timePart.split(":")
  const gregorianDate = new Date(year, month - 1, day, hour, minute, second)
  const persianDate = new PersianDate(gregorianDate)
  return persianDate.format("YYYY/MM/DD HH:mm:ss")
}

function getForPage(ev, link) {
  ev.preventDefault()
  if (!link.url || link.active) {
    return
  }

  getProducts(link.url)
}

const defaultOptions = ref({
  animationData: animationData,
})
</script>

<template>
  <b-card-body class="border-bottom-dashed border-bottom">
    <b-row class="g-3">
      <b-col xl="3">
        <div class="search-box">
          <input
            v-model="search"
            @input="handleInput"
            type="text"
            class="form-control search"
            placeholder="جستجوی نام محصول ..."
          />
          <i class="ri-search-line search-icon"></i>
        </div>
      </b-col>

      <b-col xl="9">
        <b-row class="g-3">
          <b-col sm="3">
            <select
              @change="getProducts(null)"
              v-model="productStatus"
              class="form-select mb-3"
              aria-label="Default select example"
            >
              <option value="" selected>انتخاب وضعیت محصول</option>
              <option value="0">منتشر نشده</option>
              <option value="1">منتشر شده</option>
            </select>
          </b-col>

          <b-col sm="4">
            <select
              @change="getProducts(null)"
              v-model="categoryId"
              class="form-select mb-3"
              aria-label="Default select example"
            >
              <option value="">انتخاب دسته بندی</option>
              <option
                v-for="category in categoriesWithProducts.data"
                :value="category.id"
                :key="category.id"
              >
                {{ category.name }}
              </option>
            </select>
          </b-col>

          <b-col sm="3">
            <select
              @change="getProducts(null)"
              v-model="createdBy"
              class="form-select mb-3"
              aria-label="Default select example"
            >
              <option value="">ایجاد شده توسط</option>
              <option
                v-for="creator in productCreators.data"
                :value="creator.id"
                :key="creator.id"
              >
                {{ creator.name }} {{ creator.surname }}
              </option>
            </select>
          </b-col>

          <b-col sm="2">
            <select
              @change="getProducts(null)"
              v-model="perPage"
              class="form-select mb-3"
              aria-label="Default select example"
            >
              <option value="2">2</option>
              <option value="5">5</option>
              <option value="10">10</option>
              <option value="20">20</option>
              <option value="30">30</option>
              <option value="50">50</option>
            </select>
          </b-col>
        </b-row>
      </b-col>
    </b-row>
  </b-card-body>
  <b-card-body>
    <div>
      <div class="table-responsive table-card mb-1">
        <table class="table align-middle" id="productTable">
          <thead class="table-light text-muted">
            <tr>
              <th class="sort" data-sort="product_image">تصویر</th>
              <TableHeaderCell
                field="product_name"
                :sort-field="sortField"
                :sort-direction="sortDirection"
                @click="sortProducts('product_name')"
              >
                نام محصول
              </TableHeaderCell>

              <TableHeaderCell
                field="category_id"
                :sort-field="sortField"
                :sort-direction="sortDirection"
                @click="sortProducts('category_id')"
              >
                نام دسته بندی
              </TableHeaderCell>

              <TableHeaderCell
                field="product_price"
                :sort-field="sortField"
                :sort-direction="sortDirection"
                @click="sortProducts('product_price')"
              >
                قیمت
              </TableHeaderCell>

              <TableHeaderCell
                field="discount_price"
                :sort-field="sortField"
                :sort-direction="sortDirection"
                @click="sortProducts('discount_price')"
              >
                قیمت با تخفیف
              </TableHeaderCell>

              <TableHeaderCell
                field="product_inventory"
                :sort-field="sortField"
                :sort-direction="sortDirection"
                @click="sortProducts('product_inventory')"
              >
                موجودی
              </TableHeaderCell>

              <TableHeaderCell
                field="status"
                :sort-field="sortField"
                :sort-direction="sortDirection"
                @click="sortProducts('status')"
              >
                وضعیت انتشار
              </TableHeaderCell>

              <th class="sort" data-sort="supply_and_shipping_time">
                زمان ارسال
              </th>
              <TableHeaderCell
                field="created_at"
                :sort-field="sortField"
                :sort-direction="sortDirection"
                @click="sortProducts('created_at')"
              >
                تاریخ ثبت
              </TableHeaderCell>
              <th class="sort" data-sort="action">عملیات</th>
            </tr>
          </thead>

          <tbody class="list" v-if="products.loading || !products.data.length">
            <tr>
              <td colspan="10">
                <div class="text-center">
                  <b-spinner
                    v-if="products.loading"
                    variant="dark"
                    style="width: 3rem; height: 3rem"
                  ></b-spinner>
                  <div v-else>
                    <lottie
                      class="avatar-xl"
                      colors="primary:#121331,secondary:#08a88a"
                      :options="defaultOptions"
                      :height="75"
                      :width="75"
                    />
                    <h5 class="mt-2">هیچ محصولی وجود ندارد!</h5>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>

          <tbody class="list" v-else>
            <tr v-for="(product, index) of products.data" :key="index">
              <td class="product_image">
                <div class="avatar-sm bg-light rounded p-1">
                  <img
                    :src="product.image"
                    :alt="product.name"
                    class="img-fluid d-block"
                  />
                </div>
              </td>
              <td class="product_name">
                <h5 class="fs-14 mb-1">
                  <router-link
                    :to="{
                      name: 'product.details',
                      params: { id: product.id },
                    }"
                    custom
                    v-slot="{ href }"
                  >
                    <a :href="href" target="_blank" class="link-body-emphasis">
                      {{ product.name }}
                    </a>
                  </router-link>
                </h5>
                <small class="text-muted mb-0">
                  توسط:
                  <span class="fw-medium">{{ product.created_by }}</span>
                </small>
              </td>

              <td class="category_name">{{ product.category_name }}</td>
              <td class="product_price">{{ formattedPrices[index] }}</td>
              <td class="product_discount">
                {{ product.discount !== null ? formattedDiscount[index] : "-" }}
              </td>
              <td class="product_inventory">{{ product.inventory }}</td>
              <td class="status">
                <span
                  class="badge text-uppercase"
                  :class="{
                    'badge-soft-success': product.status == 1,
                    'badge-soft-danger': product.status == 0,
                  }"
                  >{{ product.status ? "منتشر شده" : "منتشر نشده" }}</span
                >
              </td>
              <td class="supply_and_shipping_time">
                {{ product.supply_and_shipping_time }}
              </td>
              <td class="date">{{ toPersianDateTime(product.created_at) }}</td>
              <td>
                <ul class="list-inline hstack gap-2 mb-0">
                  <li
                    class="list-inline-item edit"
                    data-bs-toggle="tooltip"
                    data-bs-trigger="hover"
                    data-bs-placement="top"
                    title="ویرایش محصول"
                  >
                    <router-link
                      :to="{
                        name: 'product.edit',
                        params: { id: product.id },
                      }"
                      custom
                      v-slot="{ href }"
                    >
                      <a
                        :href="href"
                        target="_blank"
                        class="text-primary d-inline-block edit-item-btn"
                      >
                        <i class="ri-pencil-fill fs-16"></i>
                      </a>
                    </router-link>
                  </li>
                  <li
                    class="list-inline-item"
                    data-bs-toggle="tooltip"
                    data-bs-trigger="hover"
                    data-bs-placement="top"
                    title="Remove"
                  >
                    <b-link
                      class="text-danger d-inline-block remove-item-btn"
                      @click="deletedata(item)"
                    >
                      <i class="ri-delete-bin-5-fill fs-16"></i>
                    </b-link>
                  </li>
                </ul>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div
        class="d-flex justify-content-end"
        v-if="products.total > products.limit"
      >
        <div class="pagination-wrap hstack gap-2" style="display: flex">
          <ul class="pagination listjs-pagination mb-0">
            <li
              v-for="(link, i) of products.links"
              :key="i"
              class="page-item"
              :class="{
                disabled: !link.url,
                active: link.active || !link.url,
              }"
            >
              <span
                class="page-link"
                v-if="link.active"
                v-html="link.label"
              ></span>

              <a
                v-else
                href="#"
                @click="getForPage($event, link)"
                class="page-link"
                v-html="link.label"
              >
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </b-card-body>
</template>
